import { z } from 'zod';
import { TimestampSchema } from '../common.js';
/**
 * Sessions + metrics — see docs/data-model.md §4.10.
 *
 * A `Session` captures one play of a tenant-owned product (today only
 * `agent`; the enum is the extension point for future products). Lifecycle
 * events and transcript turns hang off the session by SK prefix.
 *
 * PK = TENANT#<tenantId>, SK = SESSION#<sessionId>
 * GSI3PK = PRODUCT#<productType>#<productId>
 * GSI3SK = <startedAt ISO>  (newest-first by default in the dashboard)
 *
 * TODO(phase-3-pii): evaluate GDPR + other PII handling — hash IP,
 * auto-delete, per-jurisdiction retention. Today the `ip` field stores the
 * raw first-hop X-Forwarded-For value; that is an acceptable M1 compromise
 * but must be revisited before we open this up beyond the current tenant
 * base.
 */
/** Product type the session belongs to. Extension point for future products. */
export const ProductTypeSchema = z.enum(['agent']);
/** Final outcome — only set once the session is ended. */
export const SessionOutcomeSchema = z.enum(['completed', 'abandoned', 'cta-clicked']);
/**
 * Per-session aggregate written by phase-2 ingest. Lets the dashboard render
 * "this session used N tokens / cost $X / had P95 latency Y ms" without
 * scanning every TranscriptTurn row.
 *
 * The `schemaVersion` literal lets phase-2 fold late-arriving turns in
 * atomically with `UpdateExpression ADD` only when the writer's version
 * matches the row's. A mismatch triggers a full re-aggregate on the read
 * path so we never silently lose precision after a writer upgrade.
 */
export const SessionAggregateSchema = z.object({
    totalTurns: z.number().int().nonnegative(),
    userTurns: z.number().int().nonnegative(),
    assistantTurns: z.number().int().nonnegative(),
    totalTokensInput: z.number().int().nonnegative(),
    totalTokensOutput: z.number().int().nonnegative(),
    totalTokensTotal: z.number().int().nonnegative(),
    totalCostUsd: z.number().nonnegative(),
    audioDurationMs: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    interruptionCount: z.number().int().nonnegative(),
    /**
     * Count of `search_agent_knowledge` RAG tool calls observed for this session.
     * Optional so stored aggregates created before the RAG usage slice still parse.
     */
    knowledgeSearchCount: z.number().int().nonnegative().optional(),
    avgLatencyMs: z.number().int().nonnegative(),
    p95LatencyMs: z.number().int().nonnegative(),
    schemaVersion: z.literal(1),
});
export const SessionSchema = z.object({
    sessionId: z.string().uuid(),
    tenantId: z.string().uuid(),
    productType: ProductTypeSchema,
    productId: z.string().uuid(),
    startedAt: TimestampSchema,
    endedAt: TimestampSchema.nullable(),
    durationSec: z.number().nonnegative().nullable(),
    /** Raw first-hop IP. See TODO(phase-3-pii) block above. */
    ip: z.string().nullable(),
    userAgent: z.string().max(512).nullable(),
    /** Two-letter ISO-3166-1 country code derived from CloudFront-Viewer-Country. */
    country: z
        .string()
        .regex(/^[A-Z]{2}$/, { message: 'Must be a two-letter ISO-3166-1 country code.' })
        .nullable(),
    origin: z.string().nullable(),
    outcome: SessionOutcomeSchema.nullable(),
    /**
     * Per-session usage / cost / latency aggregate. Populated by phase-2 ingest
     * at session-end (and incrementally updated as turns arrive). Optional so
     * pre-aggregate rows still parse.
     */
    aggregate: SessionAggregateSchema.optional(),
});
/**
 * List-response projection: omits the raw `ip` field (PII-adjacent; operators
 * eyeball the list for triage and don't need it there). Full detail endpoint
 * returns the complete Session.
 */
export const SessionListItemSchema = SessionSchema.omit({ ip: true });
//# sourceMappingURL=session.js.map